import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getDevice } from '../lib/adapters';
import { batteryColor, signalLabel } from '../lib/devices';
import { loadDeviceCatalog, deviceLabelFrom, deviceIconFrom } from '../lib/deviceCatalog';
import { useI18n } from '../contexts/I18nContext';
import { useRelativeTime } from '../lib/time';
import { StatusChip } from '../components/StatusChip';
import { SensorReadings } from '../components/SensorReadings';
import {
  ArrowLeft, CircleNotch, CheckCircle, XCircle, Clock,
} from '@phosphor-icons/react';

export default function DeviceDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const rel = useRelativeTime();
  const [device, setDevice] = useState(null);
  const [error, setError] = useState(null);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => { loadDeviceCatalog().then(setCatalog).catch(() => setCatalog([])); }, []);

  useEffect(() => {
    let alive = true;
    setError(null);
    setDevice(null);
    getDevice(id)
      .then((d) => {
        if (!alive) return;
        if (!d) setError({ status: 'empty', message: 'Device not found in list.' });
        setDevice(d);
      })
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        // eslint-disable-next-line no-console
        console.error('[DeviceDetail] getDevice failed', id, status, e?.message);
        setError({ status, message: e?.response?.data?.detail || e?.message || 'Request failed' });
      });
    return () => { alive = false; };
  }, [id]);

  if (error) {
    return (
      <div className="space-y-4">
        <Link to="/devices" data-testid="back-to-devices" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink">
          <ArrowLeft size={16} /> {t('devices_title')}
        </Link>
        <div className="bg-white border border-terracotta-100 rounded-2xl p-8 shadow-soft">
          <h2 className="font-heading text-xl text-terracotta-700">
            {error.status === 404 ? 'Device not found' : 'Could not load device'}
          </h2>
          <p className="text-sm text-ink-soft mt-2 font-mono">
            ID: <span data-testid="device-detail-error-id">{id}</span>
            {error.status ? ` · HTTP ${error.status}` : ''}
          </p>
          {error.message && <p className="text-sm text-ink-soft mt-1">{error.message}</p>}
        </div>
      </div>
    );
  }

  if (!device) return <div className="text-ink-muted">{t('loading')}</div>;

  const Icon = deviceIconFrom(catalog, device.type);
  const sig = signalLabel(device.signal_quality);

  return (
    <div className="space-y-8 animate-fade-up">
      <Link to="/devices" data-testid="back-to-devices" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink">
        <ArrowLeft size={16} /> {t('devices_title')}
      </Link>

      <header className="grid lg:grid-cols-[1fr_2fr] gap-6">
        <div className="bg-bg-alt rounded-2xl border border-stone_line p-8">
          <div className="w-14 h-14 rounded-2xl bg-sage-100 text-sage-600 grid place-items-center mb-4">
            <Icon size={28} weight="duotone" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold">
            {t('device_detail')}
          </div>
          <h1 className="font-heading text-3xl text-ink mt-1 leading-tight">
            {deviceLabelFrom(catalog, device.type)}
          </h1>
          <div className="mt-3"><StatusChip status={device.connectivity_status} /></div>
        </div>

        <div className="bg-white rounded-2xl border border-stone_line p-8 shadow-soft grid sm:grid-cols-2 gap-6">
          <Field label={t('device_mac')} value={<span className="font-mono">{device.mac_address}</span>} />
          <Field label={t('device_serial')} value={<span className="font-mono">{device.serial_number || device.mac_address}</span>} />
          <Field label={t('firmware')} value={device.firmware_version} />
          <Field label={t('battery')} value={<span className={batteryColor(device.battery)}>{device.battery}%</span>} />
          <Field label={t('device_signal')} value={<span className={sig.cls}>{sig.label} ({device.signal_quality}%)</span>} />
          <Field label={t('last_seen')} value={rel(device.last_seen)} />
        </div>
      </header>

      {(device.last_temperature != null || device.last_humidity != null) && (
        <SensorReadings
          temperature={device.last_temperature}
          humidity={device.last_humidity}
          at={device.last_reading_at}
          testid="device-sensor-readings"
        />
      )}

      <section className="grid lg:grid-cols-2 gap-6">
        <Card title={t('device_ota_config')}>
          <div className="space-y-4">
            <OTAStatusRow status={device.ota_status} t={t} />
            {device.ota_config.fall_sensitivity && (
              <ConfigRow label={t('fall_sensitivity')}>
                <select
                  defaultValue={device.ota_config.fall_sensitivity}
                  disabled
                  data-testid="ota-fall-sensitivity"
                  className="px-3 py-1.5 rounded-lg border border-stone_line bg-bg-alt text-sm text-ink-soft"
                >
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                </select>
              </ConfigRow>
            )}
            <ConfigRow label={t('transmission_interval')}>
              <span className="text-sm text-ink-soft">{device.ota_config.transmission_interval_s}s</span>
            </ConfigRow>
            <ConfigRow label={t('low_battery_threshold')}>
              <span className="text-sm text-ink-soft">{device.ota_config.low_battery_threshold}%</span>
            </ConfigRow>
            <div className="text-[11px] text-ink-muted pt-2 border-t border-stone_line">
              OTA deployment becomes interactive in Phase 2.
            </div>
          </div>
        </Card>

        <Card title={t('device_firmware_history')}>
          <ul className="space-y-2" data-testid="firmware-history">
            {device.firmware_history.map((h, i) => (
              <li
                key={`${h.version}-${i}`}
                className="flex items-center justify-between p-3 rounded-xl border border-stone_line"
              >
                <div>
                  <div className="text-sm font-medium text-ink font-mono">{h.version}</div>
                  <div className="text-xs text-ink-muted">{rel(h.deployed_at)}</div>
                </div>
                <FirmwareStatusBadge status={h.status} />
              </li>
            ))}
          </ul>
        </Card>
      </section>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold">{label}</div>
      <div className="text-sm text-ink mt-1.5">{value}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-stone_line p-6 shadow-soft">
      <h2 className="font-heading text-xl text-ink mb-4">{title}</h2>
      {children}
    </div>
  );
}

function ConfigRow({ label, children }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-sm text-ink">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function OTAStatusRow({ status, t }) {
  const map = {
    idle: { Icon: Clock, label: t('ota_idle'), cls: 'text-ink-muted bg-bg-alt' },
    pending: { Icon: CircleNotch, label: t('ota_pending'), cls: 'text-[#b4843f] bg-amber_warn-100' },
    deploying: { Icon: CircleNotch, label: t('ota_deploying'), cls: 'text-sage-600 bg-sage-100 animate-pulse' },
    failed: { Icon: XCircle, label: t('ota_failed'), cls: 'text-terracotta-700 bg-terracotta-100' },
    success: { Icon: CheckCircle, label: 'OK', cls: 'text-sage-600 bg-sage-100' },
  };
  const m = map[status] || map.idle;
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${m.cls}`} data-testid="ota-status">
      <m.Icon size={14} weight="duotone" />
      {t('device_ota_status')}: {m.label}
    </div>
  );
}

function FirmwareStatusBadge({ status }) {
  const map = {
    active: { cls: 'bg-sage-100 text-sage-700', label: 'active' },
    rolled_back: { cls: 'bg-terracotta-100 text-terracotta-700', label: 'rolled back' },
    archived: { cls: 'bg-bg-alt text-ink-soft', label: 'archived' },
  };
  const m = map[status] || map.archived;
  return <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded-full ${m.cls}`}>{m.label}</span>;
}
